"use client";

import { Editor, Frame, SerializedNodes } from "@craftjs/core";

import {
  EmailInput,
  TextArea,
  TextInput,
} from "@/src/components/Fields/TextField";
import { ProposalTable } from "@/src/components/ProposalTable";
import { NumberInput } from "@/src/components/Fields/NumberField";
import { RatingInput } from "@/src/components/Fields/RatingField";
import { CheckboxWithHint as Checkbox } from "@/src/components/Fields/CheckBox";
import { Action as Button } from "@/src/components/Action";
import { IFrame } from "@/src/components/IFrame";
import { TabsSelector as Tabs } from "@/src/components/Tabs";
import { Typography } from "@/src/components/Typography";
import { MultiSelect, Select } from "@/src/components/Fields/SelectField";
import { UserSelect } from "@/src/components/Fields/SelectCounterField";
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import {
  DateInput,
  DateTimeInput,
  YearInput,
} from "@/src/components/Fields/DataPickerField";
import { Steps } from "@/src/components/Stepper";
import { useWebPageComponent } from "@/src/hooks/useWebPageComponent";
import { DiiaSharing } from "@/src/components/DiiaSharing";
import { HintCardClientWrapper as HintCard } from "@/src/components/HintCard";
import { OrderCard } from "@/src/components/OrderCard";
import { useQuery } from "@tanstack/react-query";
import { ProcessInstanceType } from "@/src/shared/schemas";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import useMutableSearchParams from "@/src/hooks/useMutableSearchParams";
import { CrossProductCard } from "@/src/components/CrossProductCard";
import { KeyValue } from "@/src/components/OrderData";
import { OrderPreview } from "@/src/components/OrderPreview";
import { ProductCarousel } from "@/src/components/ProductCarouselDemo";
import { InsuredTouristsList } from "@/src/components/InsuredTourist";
import { useContextStore } from "@/src/stores/useContextStore";
import { PROCESS_INSTANCE_QUERY } from "@/src/shared/const";
import { getProcessInstance } from "@/src/api/server-actions/processInstance";
import { isNil } from "lodash";
import { useStore } from "zustand";
import { rulesStore } from "@/src/stores/useRulesStore";
import { useHandleBlocklyFields } from "@/src/hooks/blockly/useHandleBlocklyFields";
import { useProcessInstanceStore } from "@/src/stores/useProcessInstanceStore";
import { WelcomeCard } from "@/src/components/WelcomeCard";
import { RatingCard } from "@/src/components/RatingCard";
import { List } from "@/src/components/List";
import { FaqList } from "@/src/components/FaqList";
import { Title } from "@/src/components/Title";
import { Text } from "@/src/components/Text";
import { Pdf } from "@/src/components/Pdf";
import { Grid } from "@/src/components/Grid";
import { Divider } from "@/src/components/Divider";
import { Collapses } from "@/src/components/Collapses";
import { Alert } from "@/src/components/Alert";
import { QRCode } from "@/src/components/QRCode";
import { CarouselPrimitive } from "@/src/components/CarouselPrimitive";
import { ProductCardDemo as ProductCard } from "@/src/components/ProductCardDemo/ProductCardDemo";
import { TextWithValue } from "@/src/components/TextWithValue";
import { Modal } from "@/src/components/Modal";

import { DevTool } from "@hookform/devtools";
import { ApicallsModal } from "./apiCalls/ApicallsModal";
import { formatContextVariablesObjectToFormValues } from "@/src/shared/utils/form";
import { getContextVariablesToUpdate } from "@/src/components/pages/StepPage/components/apiCalls/utils";
import {
  parseToKyiv,
  nowInKyiv,
  isBefore
} from "@/src/shared/utils/dateTime";
import { normalizedDateFormat } from "@/src/shared/utils/parseDatePickerValue";
import { Image } from "@/src/components/Image";
import { Box } from "@/src/components/Box";
import { Link } from "@/src/components/Link";
import { Icons } from "@/src/components/Icons";
import { Section } from "@/src/components/Section";
import { Card } from "@/src/components/Card";
import { Promocode } from "@/src/components/Promocode/Promocode";
import {
  Banner,
  Carousel,
  CheckboxCard,
  Checkboxes,
  Footer,
  Header,
  InsuranceCard,
  ProductTabs,
  TouristData,
} from "@/src/components/pages/StepPage/components/todoComponents";
import { Container } from "@/src/components/Container";
import { FlexBox } from "@/src/components/FlexBox";
import { Navigation } from "@/src/components/Navigation";
import { Spacer } from "@/src/components/Spacer";
import { Accordion } from "@/src/components/Accordion";
import { AutoNumberInput } from "@/src/components/Fields/AutoNumberField";
import { PhoneInput } from "@/src/components/Fields/PhoneField";
import { RadioCard } from "@/src/components/RadioCard";
import { RadioButtons } from "@/src/components/Fields/RadioGroup";
import { useNextNavigation } from "@/src/components/Action/useNavigation";
import { useFieldDebouncedCallback, useUpdateField } from "@/src/hooks/useFieldDebouncedCallback";
import {
  closeLoadingModal,
} from "@/src/components/pages/StepPage/components/apiCalls/modalStore";
import { TouristOrderInfo } from "@/src/components/Fields/TouristOrderInfo";
import { useBillingPollingSSE } from "@/src/components/pages/StepPage/components/useBillingPollingSSE";
import { addStepAnalytic, analyticsStore } from "@/src/stores/useAnalyticsStore";
import { useAnalytics } from "@/src/components/pages/StepPage/components/useAnalytics";
import { useTimeSpentStore } from "@/src/stores/useTimeSpentStore";
import useAddSessionTimeOnVisibility from "@/src/hooks/useAddSessionTimeOnVisibility";
import { isRealDate } from "@/src/shared/utils/dateUtils";

interface StepPageProps {
  processInstance: ProcessInstanceType;
  processInstanceId: string;
}

// TODO: refactor: workarounds calculate endDate based on startDate and insurancePeriod
const useEndDate = () => {
  const { control, watch, setValue, getValues } = useFormContext();
  const updateField = useUpdateField();
  const debouncedUpdateField = useFieldDebouncedCallback("endDate", 300);
  const debouncedUpdateInsurancePeriod = useFieldDebouncedCallback("insurancePeriod", 300);
  const processInstanceStatus = useProcessInstanceStore(
    (state) => state.processInstance.order.status,
  );

  const startDate = useWatch({ control, name: "startDate" });
  const insurancePeriod = useWatch({ control, name: "insurancePeriod" })?.value;
  const datePeriod = useWatch({ control, name: "datePeriod" })?.value;
  const travelType = useWatch({ control, name: "travelType" })?.value;
  const daysAbroad = useWatch({ control, name: "daysAbroad" });
  const travelHall = useWatch({ control, name: "travelHall" });
  const getEndDate = (startDate: string, period: string) => {
    if (period === "SKIP_UPDATE") return "SKIP_UPDATE";
    if (!startDate || !period) return;
    // parse period to count and unit. ex: 12Months -> [12, Months]
    const [count, unit] = period.split(new RegExp("(?=[A-Z])"));
    const date = parseToKyiv(startDate);
    if (!date) return "";
    return date
      .plus({ [unit.toLowerCase()]: Number(count) })
      .minus({ days: 1 })
      .toFormat(normalizedDateFormat);
  };

  const updateEndDate = (startDate: string, endDate: string | undefined) => {
    if (endDate === "SKIP_UPDATE") return;
    if (endDate && isRealDate(endDate)) {
      setValue("endDate", endDate);
      debouncedUpdateField(endDate);
    } else {
      if (startDate === undefined) return;
      setValue("endDate", "");
      debouncedUpdateField("");
    }
  };

  const getPeriod = (values: Record<string, any>) => {
    const flowCode = values.flowCode;
    if (flowCode === "VOSCPVVNTZ") {
      return values.datePeriod?.value;
    }
    if (flowCode === "VZK_NEW") {
      if (values.travelType?.value === "multi-time") {
        return "12Months";
      }
      if (values.daysAbroad && processInstanceStatus !== "CALCULATION") {
        return travelHall ? `${Number(values.daysAbroad) + 15}Days` : `${values.daysAbroad}Days`;
      }
      return "SKIP_UPDATE";
    }
    return values.insurancePeriod?.value;
  };

  const period = useMemo(() => {
    return getPeriod(getValues());
  }, [insurancePeriod, datePeriod, travelType, daysAbroad, travelHall]);

  useEffect(() => {
    const updatePeriod = () => {
      const period = getPeriod(getValues());
      const endDate = getEndDate(startDate, period);
      updateEndDate(startDate, endDate);

      // sync insurancePeriod with datePeriod, if flowCode is VOSCPVVNTZ
      const flowCode = getValues("flowCode");
      if (flowCode === "VOSCPVVNTZ") {
        const period = getValues("datePeriod");
        setValue("insurancePeriod", period);
        debouncedUpdateInsurancePeriod(period);
      }
    };
    updatePeriod();
    return () => updatePeriod();
  }, [period, startDate]);

  const loadStartDate = useRef(false);

  useEffect(() => {
      if (!startDate || loadStartDate.current) {
          return;
      }
      loadStartDate.current = true;
      const resetStartDate = async () => {
          const flowCode = getValues("flowCode");
          const placeOfStayAbroad = getValues("placeOfStayAbroad");
          /**
           * Determines the minimum allowable issue date for a contract based on the flow code and the applicant's place of stay.
           *
           * For flowCode === "VZK_NEW":
           *   - If placeOfStayAbroad is "За межами України" (Abroad), the contract can be issued no earlier than today + 3 days.
           *   - If placeOfStayAbroad is "В Україні" (In Ukraine), the contract can be issued starting from today.
           *
           * For any other flowCode:
           *   - The contract can be issued no earlier than today + 1 day.
           *
           * The 'requiredDate' is calculated by adding the respective number of days to the current date.
           * The 'isInvalid' flag checks if the provided startDate is before the calculated requiredDate,
           * indicating that the contract issue date is not valid.
           */
          const requiredDate = nowInKyiv().plus({
              days: flowCode === "VZK_NEW"
                  ? (placeOfStayAbroad?.value === "За межами України" ? 3 : 0)
                  : 1
          });

          const isInvalid = isBefore(startDate, requiredDate, "days");
          if (isInvalid) {
              setValue("startDate", "");
              await updateField("startDate", "");
              setValue("endDate", "");
              await updateField("endDate", "");
          }
      }
      resetStartDate();
  }, [startDate])
};

const useHandleLastStep = () => {
  const {
    formState: { dirtyFields },
    getValues,
  } = useFormContext();

  const [hasContractBeenSigned, setHasContractBeenSigned] = useState(false);

  const processInstanceId = useProcessInstanceStore(
    (state) => state.processInstance.id,
  );

  const { mutateAsync: mutateNext } = useNextNavigation();
  const handleNext = useCallback(async () => {
    if (processInstanceId) {
      const payload = {
        processInstanceId,
        contextVariablesToUpdate: getContextVariablesToUpdate(
          getValues(),
          dirtyFields,
        ),
        timeSpent: useTimeSpentStore.getState().getTimeSpent(),
      };
      await mutateNext(payload);
    }
  }, [processInstanceId]);

  const handleNavigateAfterBilling = useCallback(async () => {
    if (!hasContractBeenSigned) {
      setHasContractBeenSigned(true);
      await handleNext();
      closeLoadingModal();
    } else {
      console.warn("CREATE_AND_SIGN_CONTRACT has been called already");
    }
  }, [hasContractBeenSigned]);

  useBillingPollingSSE({
    handleNavigateAfterBilling,
  });
};

const StepPageContent = () => {
  const activePage = useProcessInstanceStore(
    (state) => state.processInstance.activeStep.page,
  );
  const variables = useContextStore((state) => state.variables);
  const { reset } = useFormContext();
  const denormalizeData = useWebPageComponent(activePage);

  const { rules } = useStore(rulesStore);
  const { formFields } = useHandleBlocklyFields({
    fields: (denormalizeData || {}) as SerializedNodes,
    rules: rules,
  });
  useEndDate();
  useHandleLastStep();

  useEffect(() => {
    if (variables) {
      const formValues = formatContextVariablesObjectToFormValues(variables);
      console.log("StepPageContent useEffect", formValues, variables);
      reset({ ...formValues });
    }
  }, [variables, reset]);

  return (
    <>
      <Frame data={formFields} />
      <ApicallsModal />
    </>
  );
};

export const StepPage: FC<StepPageProps> = ({
  processInstance: _processInstance,
  processInstanceId,
}) => {
  const { stepAnalytics } = useStore(analyticsStore);
  const { callAnalyticEvent } = useAnalytics();
  const { expireSessions, setCurrentPageId, setCurrentInstanceId } = useTimeSpentStore();
  useAddSessionTimeOnVisibility();
  const prevNameRef = useRef<string>();

  const { data } = useQuery({
    queryKey: ["processInstance", processInstanceId],
    queryFn: () => getProcessInstance({ processInstanceId }),
    enabled: !!processInstanceId && !isNil(processInstanceId),
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
  const processInstance = data || _processInstance;

  const { setSearchParams } = useMutableSearchParams();
  const replaceVariablesObject = useContextStore(
    (state) => state.replaceVariablesObject,
  );

  const setProcessInstance = useProcessInstanceStore(
    (state) => state.setProcessInstance,
  );
  const activePage = useProcessInstanceStore(
    (state) => state.processInstance.activeStep.page.name,
  );

  useEffect(() => {
    if (stepAnalytics) {
      callAnalyticEvent(stepAnalytics);
      addStepAnalytic(null);
    }
  },[stepAnalytics])

  useEffect(() => {
    console.log("StepPage useEffect", processInstance);
    setSearchParams(PROCESS_INSTANCE_QUERY, processInstanceId, "replace");
    setCurrentInstanceId(processInstanceId);
    replaceVariablesObject(processInstance.contextInstance.variables);
    setProcessInstance(processInstance);
  }, [processInstanceId, processInstance]);

  useEffect(() => {
    if (activePage) {
      setCurrentPageId(activePage);
    }
  }, [activePage]);

  useEffect(() => {
    expireSessions();
  }, []);

  const form = useForm({
    mode: "onChange",
  });

  return (
    <FormProvider {...form}>
      <Editor
        resolver={{
          FlexBox,
          Text,
          Button,
          TextInput,
          TextArea,
          EmailInput,
          NumberInput,
          PhoneInput,
          RatingInput,
          AutoNumberInput,
          IFrame,
          QRCode,
          Alert,
          Image,
          Pdf,
          Tabs,
          Steps,
          Title,
          List,
          KeyValue,
          DiiaSharing,
          Link,
          Icons,
          Navigation,
          DateInput,
          DateTimeInput,
          YearInput,
          Checkboxes,
          RadioButtons,
          Select,
          MultiSelect,
          UserSelect,
          RadioCard,
          CheckboxCard,
          Divider,
          Section,
          Container,
          Grid,
          Box,
          Card,
          Spacer,
          Collapses,
          Accordion,
          Header,
          Footer,
          FaqList,
          ProductCard,
          OrderCard,
          HintCard,
          RatingCard,
          CrossProductCard,
          InsuranceCard,
          Carousel,
          Banner,
          Checkbox,
          ProposalTable,
          Promocode,
          TouristData,
          Typography,
          InsuredTouristsList,
          TouristOrderInfo,
          WelcomeCard,
          ProductCarousel,
          ProductTabs,
          OrderPreview,
          CarouselPrimitive,
          TextWithValue,
          Modal,
        }}
        enabled={false}
        key={activePage}
      >
        {processInstance.id && <StepPageContent />}
      </Editor>
      <DevTool control={form.control} />
    </FormProvider>
  );
};
